import Link from "next/link";
import Marquee from "react-fast-marquee";
import type { NextPage } from "next";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { authFormState } from "../Atoms/auth/AuthFormState";
import AuthCard from "../components/auth/AuthCard";
import { API_URL, AppConfig } from "../config";
import { useToast } from "@/components/ui/use-toast";
import { getSession } from "next-auth/react";
import axios from "axios";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { cn } from "@/lib/utils";
import GoogleAnalytics from "@/components/GA";
import { useRouter } from "next/router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BsGlobe } from "react-icons/bs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import InstallPWAButton from "@/components/common/InstallPWAButton";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import CustomMainPage from "@/components/blocks/broker-custom/CustomMainPage";
import { Separator } from "@/components/ui/separator";


const Home: NextPage = (props: any) => {
  const router = useRouter();
  const { toast } = useToast();
  const [showLanguageMenu, setShowLanguageMenu] = useState<boolean>(false);
  const [enabledSwitch, setEnabledSwitch] = useState(
    router.query.locale || "en",
  );
  const [formState, setFormState] = useRecoilState(authFormState);
  const { darkTheme } = useDarkTheme();
  const { t } = useTranslation("layout-page");
  const { details } = useAppDetails();

  const selectedLanguage = useMemo(
    () => router.query.locale || enabledSwitch,
    [router.query.locale, enabledSwitch],
  );

  const languagesOptions = useMemo(
    () => [
      { name: t("Bahasa Indonesia"), value: "id" },
      { name: t("Bahasa Malaysia"), value: "ms" },
      { name: t("Chinese"), value: "zh" },
      { name: t("English"), value: "en" },
      { name: t("German"), value: "de" },
      { name: t("Italian"), value: "it" },
      { name: t("Portuguese"), value: "pt" },
      { name: t("Spanish"), value: "es" },
      { name: t("Japanese"), value: "ja" },
      { name: t("Thai"), value: "th" },
      { name: t("Vietnamese"), value: "vi" },
    ],
    [t],
  );

  useEffect(() => {
    if (props?.user !== null) {
      if (localStorage && props?.user?.user) {
        localStorage.setItem("user-info", JSON.stringify(props?.user));
        localStorage.setItem("userAuthenticated", "true");
        if (
          props?.user.subscription &&
          props?.user.subscription == "inactive"
        ) {
          router.push(`/subscription?locale=${router.query.locale || "en"}`);
        }
        router.push(`/build?locale=${router.query.locale || "en"}`);
      }
    }
  }, []);

  const generateScriptCode = () => {
    return {
      token: "",
      user_id: "4VXr2i",
      user_name: "Anonymous",
      demo_mode: true,
      unique_id: "9cf4d5d9-15e1-45d4-8f8f-289f10eb1444",
      logoVisible: true,
    };
  };

  const toastMessage = (status: boolean, message: string) => {
    if (status == true) {
      toast({
        title: message,
      });
      return;
    } else {
      toast({
        title: message,
        variant: "destructive",
      });
    }
  };

  if (details?.whiteLableOptions?.loginSignupPage == "custom") {
    return <CustomMainPage></CustomMainPage>;
  }
  if (details?.whiteLableOptions?.loginSignupPage == "hidden") {
    return <></>;
  }

  return (
    <>
      <Head>
        {process.env.NODE_ENV === "production" ? (
          <meta name="robots" content="index,follow" />
        ) : (
          <meta name="robots" content="noindex,nofollow" />
        )}
        <GoogleAnalytics GA_TRACKING_ID={"G-NRPKERR6KT"} />
        <meta charSet="UTF-8" key="charset" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          key="viewport"
        />
        <link
          rel="stylesheet"
          href="https://rsms.me/inter/inter.css"
          key="inter-font"
        />
        <link rel="stylesheet" href="/canvas/edit/new/level2-index.css" />
      </Head>
      <NextSeo
        title={AppConfig.title}
        description={AppConfig.description}
        openGraph={{
          locale: AppConfig.locale,
          site_name: AppConfig.site_name,
        }}
      />

      <div className="relative min-h-screen flex flex-col bg-[#050B16] overflow-hidden">
        {/* Top Promo Banner */}
        <div className="w-full bg-[#2563EB] text-white text-center py-1.5 sm:py-2.5 px-4 text-xs sm:text-sm font-semibold z-10 shadow-sm">
          {t("Try free for 30 days — No credit card required.")}
        </div>
        {/* Background GIF */}
        <div className=" absolute top-0 left-0 w-full h-full opacity-70 hidden md:block">
          <img
            src="/login-bg.gif"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-full  backdrop-blur-sm opacity-70 bg-blue-900/10"></div>

        {/* Centered Modal Overlay */}
        <div className="flex-grow flex flex-col items-center justify-center px-4 py-6 sm:py-12 z-10">
          {/* Logo, Title & Subtitle ABOVE the card */}
          <div className="w-full max-w-[680px] text-center mb-5 sm:mb-8 flex flex-col items-center">
            <img
              src={details?.brokerLogo ?? "/images/logos/Logo_Dark.png"}
              className="max-h-8 sm:max-h-10 w-auto object-contain"
              alt="Logo"
            />
          </div>

          <div
            className={cn(
              "w-full bg-[#081125]/75 border border-slate-800 rounded-2xl p-5 sm:p-8 shadow-2xl flex flex-col items-center backdrop-blur-md transition-all duration-300 mb-8",
              formState.view === "create" ? "max-w-[540px]" : "max-w-[420px]",
            )}
          >
            <div className="flex flex-col space-y-2 text-center mb-6">
              {formState.view === "login" ? (
                <>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    {t("Welcome back")}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {t("Sign in to continue your trading journey")}
                  </p>
                </>
              ) : (
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  {formState.view === "resetPassword"
                    ? t("Reset your password")
                    : formState.view === "create"
                      ? t("Create an account")
                      : t("Verify your login attempt")}
                </h1>
              )}
            </div>

            <div className="w-full">
              <AuthCard toast={toastMessage} />
            </div>
          </div>
        </div>

        {/* Sticky Footer Disclaimer */}
        <div className="w-full bg-[#0c1938]/95 border-t border-[#1d3570]/70 py-2.5 px-4 text-[11px] text-slate-300/90 fixed bottom-0 left-0 z-20 backdrop-blur-sm overflow-hidden flex items-center">
          <Marquee speed={30} gradient={false} pauseOnHover className="text-[11px]">
            <span className="font-semibold text-blue-400 uppercase tracking-wider mr-1">
              Disclaimer:
            </span>{" "}
            <span className="mr-8">
              Level2 provides software for designing, backtesting and automating
              trading strategies. It does not provide financial, investment, or
              trading advice. Trading involves risk, and past performance does not
              guarantee future results.
            </span>
          </Marquee>
        </div>

        <InstallPWAButton />
      </div>
    </>
  );
};
export const getServerSideProps = async (ctx: any) => {
  let session: any = await getSession(ctx);
  let res = null;
  if (session) {
    let url = new URL("social_login", API_URL);
    url.searchParams.append("token", session.token.access_token);

    res = await axios
      .get(url.toString())
      .catch((e) => {
        console.error(e);
        return { data: {} };
      })
      .finally(() => ({ data: {} }));
  }
  return {
    props: {
      user: res?.data || null,
      ...(await serverSideTranslations(ctx.locale || "en", [
        "broker-page",
        "broker-page",
        "common-page",
        "layout-page",
      ])),
    },
  };
};

export default Home;
